/* global isJest */
import React, { useState, useEffect, useRef, useContext } from 'react';
import Head from 'next/head';
import PropTypes from 'prop-types';
import Vimeo from '@u-wave/react-vimeo';
import BackgroundImage from '../BackgroundImage';
import ImageCaption from '../ImageCaption';

import { serializeImage } from '../../utils/SerializeImage';
import useMediaQuery from '../../utils/useMediaQuery';
import isEmpty from '../../utils/isEmpty';
import { kebabToPascal } from '../../utils/caseconverters';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { breakpoints } from '../../constants';
import { CookieStateContext } from '../../containers/BasePage/state';
import OriginalLogo from '../../public/img/logotype-visit-original-sweden.svg';

import Icon from '../Icon';
import Button from '../Button';
import Image from 'next/image';

import styles from './Hero.module.scss';

const Hero = ({
    title = '',
    text,
    longTitle = '',
    ctaUrl,
    image = {},
    logo = {},
    videoUrl = '',
    hideContent = false,
    pageType,
    mapDocument = '',
    mapLabel = '',
    isBelowBreadcrumbs = false,
    headerVariant = 'default',
    textTheme = 'default',
    layout = 'default',
}) => {
    const { t } = useTranslation('common');

    const heroRef = useRef(null);
    const { state } = useContext(CookieStateContext) || {};

    const [isMounted, setIsMounted] = useState(false);
    const [isMediaQueriesReady, setIsMediaQueriesReady] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(true);
    const [videoStarted, setVideoStarted] = useState(false);
    const [isPlaying, setIsPlaying] = useState(true);
    const [vimeoPlayer, setVimeoPlayer] = useState(null);

    const isMobile = useMediaQuery({
        query: `(max-width: ${breakpoints.m - 1}px)`,
    });
    const isPortrait = useMediaQuery({
        query: `(max-width: 549px)`,
    });

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Check when media queries are ready and show background image again if portrait image is used
    useEffect(() => {
        setIsMediaQueriesReady(true);
    }, [isPortrait]);

    if (isEmpty(image)) {
        return null;
    }

    const cookieConsent = state?.categories?.includes('targeting');

    const isCampaign = pageType === 'CampaignPage';
    const hideNavigation = headerVariant === 'hide';
    const showMinimalHeader = headerVariant === 'minimal';
    const textThemeModifier = textTheme ? kebabToPascal(textTheme) : '';

    // Possibility to name layout with specific sub theme e.g. "content-half-right--brand-yellow"
    const layoutWithTheme = layout.split('--');
    const layoutModifier = layout && layoutWithTheme.length ? kebabToPascal(layoutWithTheme[0]) : '';
    const layoutThemeModifier = layout && layoutWithTheme.length > 1 ? kebabToPascal(layoutWithTheme[1]) : '';

    const hasLogo = logo && logo?.src;
    const hasOriginalSwedenLogo = layout === 'original-sweden-logo';

    // Hero is much taller in mobile, and images usually landscape, increase width for mobile to prevent lower quality images
    const serializedImage = serializeImage(image, true);
    const portraitImage = image?.renditions?.portrait ? image?.renditions?.portrait : null;

    // Always load portrait image in mobile and desktop first, switching to desktop image when needed
    // Only home and campaign pages have portrait image rendition
    const showPortraitImage =
        serializedImage && portraitImage && (!isMounted || isPortrait || typeof isJest !== 'undefined');
    const backgroundImage = {
        ...serializedImage,
        ...(showPortraitImage && { ...portraitImage, quality: 60 }),
        ...(isCampaign && { quality: 90 }),
    };
    const sizes = showPortraitImage ? '100vw' : '(min-width: 416px) 100vw, 120vw';
    const mapImageSizes = '(min-width: 768px) 120px, 85px';

    const displayText = text.replaceAll('\r\n', '<br>');
    const isVimeo = videoUrl?.includes('vimeo.com');
    const isDevelopment = process.env.NODE_ENV === 'development';

    const scrollDown = () => {
        if (typeof window !== 'object') {
            return;
        }
        const heroHeight = heroRef.current.scrollHeight;
        window.scroll({ top: heroHeight, behavior: 'smooth' });
    };

    const togglePlayPause = () => {
        if (vimeoPlayer) {
            if (isPlaying) {
                vimeoPlayer.pause();
            } else {
                vimeoPlayer.play();
            }
        }
    };

    const classes = classNames(
        styles['Hero'],
        { [styles['Hero--Text']]: title },
        { [styles['Hero--ImageLoaded']]: imageLoaded },
        { [styles['Hero--HideContent']]: hideContent },
        { [styles['Hero--NoCaption']]: !image.caption },
        { [styles['Hero--HideNavigation']]: hideNavigation },
        { [styles['Hero--MinimalHeader']]: showMinimalHeader },
        { [styles['Hero--BelowBreadcrumbs']]: isBelowBreadcrumbs },
        { [styles[`Hero--TextTheme${textThemeModifier}`]]: textThemeModifier },
        { [styles['Hero--TextThemeDefault']]: !textThemeModifier },
        { [styles[`Hero--Layout${layoutModifier}`]]: layoutModifier },
        { [styles['Hero--LayoutDefault']]: !layoutModifier },
        {
            [styles[`Hero--LayoutTheme${layoutThemeModifier}`]]: layoutThemeModifier,
        },
        { [styles[`Hero--${pageType}`]]: !!pageType },
        {
            [styles['Hero--HasPortraitImage']]: serializedImage && portraitImage,
        },
        { [styles['Hero--MediaQueryReady']]: isMediaQueriesReady }
    );
    return (
        <React.Fragment>
            {isVimeo && (
                <Head>
                    <link rel="preconnect" href="https://vimeo.com" crossOrigin="anonymous" />
                    <link rel="preconnect" href="https://player.vimeo.com" crossOrigin="anonymous" />
                    <link rel="preconnect" href="https://i.vimeocdn.com" crossOrigin="anonymous" />
                    <link rel="preconnect" href="https://f.vimeocdn.com" crossOrigin="anonymous" />
                </Head>
            )}
            <div ref={heroRef} className={classes}>
                <div className={styles['Hero__Container']}>
                    {videoUrl && (cookieConsent !== undefined || isDevelopment) && (
                        <div
                            className={classNames(styles['Hero__Video'], {
                                [styles['Hero__Video--Playing']]: videoStarted,
                            })}>
                            <Vimeo
                                className={styles['Hero__VideoPlayer']}
                                video={videoUrl}
                                muted={true}
                                controls={false}
                                volume={0}
                                autoplay={true}
                                height={'100%'}
                                width={'100%'}
                                title={''}
                                background={true}
                                onReady={(player) => setVimeoPlayer(player)}
                                onPlay={() => {
                                    setVideoStarted(true);
                                    setIsPlaying(true);
                                }}
                                onPause={() => setIsPlaying(false)}
                                onEnd={() => {
                                    setVideoStarted(false);
                                    setIsPlaying(false);
                                }}
                                dnt={!cookieConsent}
                            />
                        </div>
                    )}
                    {serializedImage && (
                        <BackgroundImage
                            loadedCallback={() => setImageLoaded(true)}
                            shouldLazyLoad={false}
                            priority={true}
                            {...backgroundImage}
                            sizes={sizes}
                            hideAlt={true}
                            className={styles['Hero__BackgroundImage']}
                        />
                    )}
                    {videoUrl && (cookieConsent !== undefined || isDevelopment) && videoStarted && (
                        <button
                            onClick={togglePlayPause}
                            className={classNames(
                                styles['Hero__VideoButton'],
                                isPlaying ? styles['Hero__VideoButton--Playing'] : styles['Hero__VideoButton--Paused']
                            )}
                            aria-label={isPlaying ? 'Pause video' : 'Play video'}>
                            {isPlaying ? 'Pause' : 'Play'}
                        </button>
                    )}
                    {title && !hideContent && (
                        <div className={styles['Hero__Wrap']}>
                            <div className={styles['Hero__Content']}>
                                <div className={styles['Hero__InnerContent']}>
                                    {hasOriginalSwedenLogo ? (
                                        <h1 className={styles['Hero__TitleWithLogo']}>
                                            <div className={styles['Hero__OriginalSwedenLogo']}>
                                                <Image
                                                    src={OriginalLogo.src}
                                                    overrideSrc={OriginalLogo.src}
                                                    alt={longTitle || title}
                                                    objectFit="contain"
                                                    width={OriginalLogo.width}
                                                    height={OriginalLogo.height}
                                                    priority={true}
                                                    unoptimized={true}
                                                />
                                            </div>

                                            {displayText && (
                                                <div
                                                    className={styles['Hero__Text']}
                                                    dangerouslySetInnerHTML={{
                                                        __html: displayText,
                                                    }}
                                                />
                                            )}
                                        </h1>
                                    ) : (
                                        <>
                                            <div className={styles['Hero__TitleWrapper']}>
                                                {hasLogo && (
                                                    <div className={styles['Hero__Logo']}>
                                                        <Image
                                                            src={logo.src}
                                                            overrideSrc={logo.src}
                                                            alt={logo?.altText || logo?.title}
                                                            objectFit="contain"
                                                            width={logo?.width}
                                                            height={logo?.height}
                                                            priority={true}
                                                            unoptimized={true}
                                                        />
                                                    </div>
                                                )}
                                                <h1
                                                    className={styles['Hero__Title']}
                                                    dangerouslySetInnerHTML={{
                                                        __html: longTitle || title,
                                                    }}
                                                />
                                            </div>
                                            {displayText && (
                                                <div
                                                    className={styles['Hero__Text']}
                                                    dangerouslySetInnerHTML={{
                                                        __html: displayText,
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}

                                    {ctaUrl && (
                                        <div className={styles['Hero__CTA']}>
                                            <Button link={ctaUrl} text={t('hero.readMore')} type={'Primary'} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {image.caption && (
                        <ImageCaption
                            id={`image-${image.id}`}
                            title={image.title}
                            caption={image.caption}
                            credits={isCampaign ? image.credits : undefined}
                        />
                    )}
                    {mapDocument && mapDocument?.src && (
                        <div className={styles['Hero__MapImageWrapper']}>
                            <div className={styles['Hero__MapImage']}>
                                <figure>
                                    <Image
                                        src={mapDocument.src}
                                        overrideSrc={mapDocument.src}
                                        width={mapDocument.width || 100}
                                        height={mapDocument.height || 100}
                                        alt={mapLabel}
                                        sizes={mapImageSizes}
                                        priority={true}
                                        quality={100}
                                    />
                                    {mapLabel && <figcaption>{mapLabel}</figcaption>}
                                </figure>
                            </div>
                        </div>
                    )}
                </div>
                {/* Leaving out scroll down button for now as per design decision
                {(isCampaign || (hideContent && !isMobile)) && (
                    <button onClick={scrollDown} className={styles['Hero__ScrollDown']}>
                        <span className="sr-only">{t('hero.scrollToContent')}</span>
                        <Icon className={styles['Hero__ScrollDown__Icon']} name="IconArrow" />
                    </button>
                )} */}
            </div>
            {!isCampaign && image.credits && (
                <div className={styles['Hero__Credits']}>{`${t('hero.photo')}: ${image.credits}`}</div>
            )}
        </React.Fragment>
    );
};

Hero.propTypes = {
    title: PropTypes.string.isRequired,
    longTitle: PropTypes.string,
    image: PropTypes.object,
    logo: PropTypes.object,
    pageType: PropTypes.string,
    layout: PropTypes.string,
    videoUrl: PropTypes.string,
    ctaUrl: PropTypes.string,
    hideContent: PropTypes.bool,
    mapDocument: PropTypes.string,
    mapLabel: PropTypes.string,
    isBelowBreadcrumbs: PropTypes.bool,
    headerVariant: PropTypes.string,
};

export default Hero;
